import { useState, useEffect } from 'react';
import { supabase, Database } from '../lib/supabase';

type Progress = Database['public']['Tables']['child_progress']['Row'];
type ProgressInsert = Database['public']['Tables']['child_progress']['Insert'];

export function useProgress(childId: string | null) {
  const [progress, setProgress] = useState<Progress[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchProgress = async () => {
    if (!childId) {
      setProgress([]);
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('child_progress')
        .select(`
          *,
          activities (
            title,
            subject,
            difficulty_level
          )
        `)
        .eq('child_id', childId)
        .order('completed_at', { ascending: false });

      if (error) throw error;
      setProgress(data || []);
    } catch (error) {
      console.error('Error fetching progress:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProgress();
  }, [childId]);

  const addProgress = async (progressData: ProgressInsert) => {
    try {
      const { data, error } = await supabase
        .from('child_progress')
        .insert(progressData)
        .select(`
          *,
          activities (
            title,
            subject,
            difficulty_level
          )
        `)
        .single();

      if (error) throw error;

      // Update child's total points
      await supabase.rpc('update_child_points', {
        child_id: progressData.child_id,
        points_to_add: progressData.points_earned || 0
      });

      // Update daily streak
      await updateDailyStreak(progressData.child_id);

      setProgress(prev => [data, ...prev]);
      return { data, error: null };
    } catch (error) {
      console.error('Error adding progress:', error);
      return { data: null, error };
    }
  };

  const updateDailyStreak = async (childId: string) => {
    const today = new Date().toISOString().split('T')[0];
    
    try {
      const { data: streakData } = await supabase
        .from('daily_streaks')
        .select('*')
        .eq('child_id', childId)
        .single();

      if (streakData) {
        const lastDate = new Date(streakData.last_activity_date);
        const todayDate = new Date(today);
        const daysDiff = Math.floor((todayDate.getTime() - lastDate.getTime()) / (1000 * 60 * 60 * 24));

        let newStreak = streakData.current_streak;
        
        if (daysDiff === 1) {
          // Consecutive day
          newStreak += 1;
        } else if (daysDiff > 1) {
          // Streak broken
          newStreak = 1;
        }
        // Same day = no change

        await supabase
          .from('daily_streaks')
          .update({
            current_streak: newStreak,
            longest_streak: Math.max(newStreak, streakData.longest_streak),
            last_activity_date: today
          })
          .eq('child_id', childId);
      }
    } catch (error) {
      console.error('Error updating daily streak:', error);
    }
  };

  const getSubjectProgress = (subject: string) => {
    return progress.filter(p => p.activities?.subject === subject);
  };

  const getAverageScore = () => {
    if (progress.length === 0) return 0;
    const total = progress.reduce((sum, p) => sum + p.score, 0);
    return Math.round(total / progress.length);
  };

  const getTotalTimeSpent = () => {
    return progress.reduce((total, p) => total + p.time_spent, 0);
  };

  return {
    progress,
    loading,
    addProgress,
    getSubjectProgress,
    getAverageScore,
    getTotalTimeSpent,
    refetch: fetchProgress,
  };
}